import { COVER_WIDTH_DEFAULT, type ShowcaseCoverWidth } from '~/components/v2/components/showcase/showcaseCoverWidth';

/*
 * The absent-value fallback for `presentationSettings.coverFade`. This MUST equal what the public
 * renderer (`PublicShowcaseCover`) shows when the field is absent: links saved before the field
 * existed have no `coverFade` and must keep rendering the cover with no fade overlay. The
 * authoring surfaces default from the same constant so the editor never shows a toggle state that
 * differs from what a legacy link actually renders. Lives in the shared collection folder (not
 * the share-modal feature folder) so the public viewer doesn't import from an authoring feature
 * tree.
 */
export const COVER_FADE_DEFAULT = false;

// Byte-pair offsets in a 6-character hex color (e.g. "1f3cbd" → 1F, 3C, BD).
const HEX_RADIX = 16;
const HEX_FULL_LENGTH = 6;
const HEX_RED_END = 2;
const HEX_GREEN_END = 4;
const HEX_BLUE_END = 6;

// The overlay is fully transparent through the top 40% of the cover, then ramps to the target
// color at the bottom edge so the image dissolves into the surface directly below it.
const toFadeGradient = (rgb: string): string => `linear-gradient(to bottom, rgba(${rgb}, 0) 40%, rgba(${rgb}, 1) 100%)`;

export type BuildCoverFadeGradientParams = {
  /** The link's cover band width mode; absent falls back to `COVER_WIDTH_DEFAULT`. */
  coverWidth: ShowcaseCoverWidth | undefined;
  /** The resolved custom page background hex (`usePublicCustomColors().pageBgColor`), if the publisher set one. */
  pageBgColor: string | undefined;
};

/*
 * Builds the cover fade overlay gradient. The fade's target color MUST match the surface the
 * cover visually sits on, which branches by cover width:
 *
 * - `inset` covers sit above the content column (bg-gray-1), so they fade into gray-1 even when a
 *   custom page background color is set — the column, not the outer page, is behind them.
 * - Full-bleed covers (`fullWidth`/`immersive`) fade into the page background: the custom
 *   `pageBgColor` when set, otherwise the page shell's bg-gray-4.
 *
 * A custom color's hex is parsed to r,g,b so the alpha-zero start stop is the same color at zero
 * opacity — plain `transparent` (rgba(0,0,0,0)) would interpolate through gray. The gray branches
 * use the `--colors-<scale>-<step>-rgb` triplet vars for the same reason, which also keeps them
 * dark-mode-aware for free.
 */
export const buildCoverFadeGradient = ({ coverWidth, pageBgColor }: BuildCoverFadeGradientParams): string => {
  if ((coverWidth ?? COVER_WIDTH_DEFAULT) === 'inset') {
    return toFadeGradient('var(--colors-gray-1-rgb)');
  }

  if (pageBgColor) {
    const hex = pageBgColor.replace('#', '');
    const r = Number.parseInt(hex.slice(0, HEX_RED_END), HEX_RADIX);
    const g = Number.parseInt(hex.slice(HEX_RED_END, HEX_GREEN_END), HEX_RADIX);
    const b = Number.parseInt(hex.slice(HEX_GREEN_END, HEX_BLUE_END), HEX_RADIX);

    if (hex.length === HEX_FULL_LENGTH && !Number.isNaN(r) && !Number.isNaN(g) && !Number.isNaN(b)) {
      return toFadeGradient(`${r}, ${g}, ${b}`);
    }
  }

  // Also the landing branch for an unparseable custom hex: the browser drops an invalid inline
  // backgroundColor on the page wrapper too, so the visible page surface is bg-gray-4 either way.
  return toFadeGradient('var(--colors-gray-4-rgb)');
};
